/**
 * Панель «Ход накопления»: накопление групп и котировка на одной оси времени.
 *
 * Чистый модуль без React по правилу lib/*.ts: vitest в этом проекте включает
 * только `*.test.ts`, и всё, что решает, КАКОЕ число и какую форму видит
 * оператор, обязано лежать под тестом.
 *
 * # Зачем панель
 *
 * Сводка групп отвечает «сколько набрано» числом на сейчас. Вопрос оператора
 * другой: как группа набирала позицию ПО ХОДУ окна и что при этом делала цена —
 * что первично, что вторично, что коррелирует.
 *
 * # Пять способов соврать этой панелью, и что с каждым сделано
 *
 *  1. ДВА ОКНА НА ОДНОМ ЭКРАНЕ. Группа назначена кошельку за ВСЮ накопленную
 *     глубину, а линия рисует накопление за ЭТО окно. Ровно та же ловушка, что
 *     у сводки групп, и разводится так же — подписью, где оба окна названы.
 *  2. РАЗНЫЕ ЧАСЫ У ДВУХ ЛИНИЙ. Сделка приезжает лентой с задержкой около
 *     секунды, котировка снимается нашим тиком со своим возрастом. Значит
 *     задержки меньше LEAD_FLOOR_S физически неразличимы, и строка «что
 *     первично» обязана говорить «не различимо», а не печатать число. Иначе
 *     панель отвечает на ГЛАВНЫЙ вопрос величиной, которой не существует.
 *  3. ПРОПУСК КАК ТИШИНА. У накопления тихая секунда — ровная линия (между
 *     сделками набранное не меняется), у котировки пропуск — РАЗРЫВ линии
 *     (между образцами цена неизвестна). Соедини второе — и получится
 *     нарисованное движение цены, которого никто не наблюдал.
 *  4. ФОРМА БЕЗ ЗНАМЕНАТЕЛЯ. «Линия ровная» на трёх сделках и на трёх тысячах —
 *     разные утверждения. Число сделок и кошельков стоит основанием ПЕРЕД
 *     графиком.
 *  5. ЦВЕТ КАК ОЦЕНКА. Знак накопления цветом НЕ кодируется — то же правило,
 *     что установила sharesColor: набранное не есть прибыль, и зелёное на плюсе
 *     приписало бы группе оценку, которой в числе нет. Знак читается положением
 *     относительно нуля.
 */
import type {
  Order,
  PaperOrder,
  PaperStatus,
  Timeline,
  TimelineIndexPoint,
  TimelinePoint,
  TimelineQuote,
} from "../api/types";
import { isTerminal } from "../store/orders";
import { formatTime, plural } from "./format";
import { div, groupThousands, parse, roundToFixed, SCALE, type Micro } from "./money";
import { issuerLabel, notional } from "./order";
import { formatDollars, formatShares } from "./tape";
import { REAL_DATA_LAG_S } from "./twaplock";
import { formatIndex } from "./valuation";

/** Ширина холста в единицах viewBox. Не пиксели: SVG тянется по контейнеру. */
export const PLOT_W = 1000;
/** Высота полотна цены и полотна накопления. */
export const PRICE_H = 110;
/**
 * Полотно накопления выше полотна цены: линий на нём пять — четыре
 * составляющие и итог, — и на ста тридцати пикселях они слипались в жгут.
 */
export const EDGE_H = 170;
/**
 * Полотно ставок ниже прочих: на нём читают не уровень, а МОМЕНТ и ВЫСОТУ
 * всплеска, и на девяноста пикселях это видно так же, как на ста семидесяти.
 */
export const FLOW_H = 90;
/**
 * Полотно индекса: семь линий в долларах — S, Ŝ t−3, достройка, прогноз
 * страйка, страйк, сырой Chainlink и биржа, — и на высоте цены они слипались бы:
 * разница между S и Ŝ это единицы долларов при размахе окна в десятки. Биржа
 * и сырой ряд уходят от S дальше всех и растягивают шкалу — отсюда запас
 * высоты сверх прежних ста тридцати.
 */
export const INDEX_H = 160;
/** Полотно отклонения и запаса: три линии вокруг нулевой черты. */
export const GAP_H = 110;

/**
 * Пол размаха шкалы индекса — доля уровня.
 *
 * Долей, а не долларами: у BTC и ETH уровни различаются в двадцать пять раз, и
 * пол в долларах на одном из них раздувал бы дрожание последнего знака до высоты
 * полотна, а на другом сплющивал бы окно в линию. Один базисный пункт — это
 * $7.5 на BTC и тридцать центов на ETH.
 */
const INDEX_MIN_SPAN_SHARE = 1e-4;
/** Пол размаха отклонения и запаса — пятая часть базисного пункта. */
const GAP_MIN_SPAN_SHARE = 2e-5;

/**
 * Сколько секунд пропуска линия индекса ещё соединяет.
 *
 * Журнал пишется раз в секунду по такту трейдера, и такт плавает: две записи
 * ложатся в одну корзину, соседняя остаётся пустой. Рвать линию на каждой такой
 * корзине значило бы нарисовать пунктир там, где ряд цел. Дальше двух секунд —
 * уже молчание публикатора, и там линия рвётся: между записями значение
 * неизвестно.
 */
const INDEX_GAP_TOLERANCE_S = 2;

/**
 * Пол разрешения по времени, секунды.
 *
 * Ниже него «кто раньше» не измеряется НИЧЕМ: замер пути «биржа → Redis» дал
 * p50 около 0.9 с у ленты, а у котировки свой возраст стакана. Две секунды —
 * это их сумма, округлённая вверх до целого шага сетки.
 */
export const LEAD_FLOOR_S = 2;

/** Дальше этого сдвига корреляцию не ищем: пятиминутка — 300 секунд. */
export const LEAD_MAX_S = 30;

/**
 * Сколько пар точек нужно, чтобы вообще произносить слово «коррелирует».
 *
 * Тридцать — не порог значимости, а порог осмысленности: на десяти точках
 * коэффициент скачет от −0.9 до +0.9 от одной сделки, и печатать его значило бы
 * выдавать шум за измерение.
 */
export const LEAD_MIN_POINTS = 30;

/** Минимальный размах шкалы цены: иначе панель увеличивала бы дрожание тика. */
const PRICE_MIN_SPAN = 0.1;

export interface TimelineLine {
  /** Ключ серии и он же ключ узла: up, down, group, market. */
  key: string;
  /** Отрезки пути. Их несколько, когда ряд рвётся пропуском. */
  paths: string[];
  /** Токен цвета целиком, как его понимает SVG: `var(--color-…)`. */
  color: string;
  label: string;
  /** Последнее известное значение строкой — для легенды. */
  value: string;
  /** Пунктир: им отличается фоновая линия рынка от линии выбранной группы. */
  faint: boolean;
  /**
   * Образец в легенде — точка или кольцо вместо штриха. Есть только у строк,
   * которые подписывают точки ордеров: штрих у них читался бы как ещё одна
   * линия, которой на полотне нет.
   */
  swatch?: "dot" | "ring";
}

export interface TimelineTick {
  /** Координата в единицах viewBox. */
  at: number;
  label: string;
  /**
   * Куда прижимать подпись.
   *
   * Крайние деления стоят на границах холста, и подпись по центру уезжала бы
   * половиной за край: замер показал «+24 c», перенесённое на две строки. Первое
   * прижимается влево, последнее вправо, остальные центрируются.
   */
  anchor: "start" | "middle" | "end";
  /**
   * Деление без подписи — только штрих сетки. Сетка на предстарте и окне
   * остаётся пятнадцатисекундной, а подписи идут через одну: на 390 секундах
   * оси двадцать семь подписей «+2:15» слипались бы на обычной ширине карточки.
   */
  minor?: boolean;
}

/** Столбик потока: ставки одной корзины в одну сторону. */
export interface TimelineBar {
  key: string;
  /** Левый край и ширина в единицах viewBox. */
  x: number;
  w: number;
  /** Верх и высота: столбик растёт ОТ нулевой черты. */
  y: number;
  h: number;
  color: string;
}

export interface TimelineChart {
  lines: TimelineLine[];
  /**
   * Координата нулевой черты, если она у полотна есть.
   *
   * В самом полотне, а не рядом с ним: у накопления ноль посередине шкалы, у
   * оборота — по низу (величина неотрицательна), и держать это снаружи значило
   * бы, что разметка знает про смысл каждого полотна.
   */
  zeroAt?: number;
  /** Столбики. Пусто у полотен, которые рисуют линии. */
  bars: TimelineBar[];
  /** Точки наших ордеров. Есть только у полотна цены. */
  marks?: TimelineMark[];
  ticks: TimelineTick[];
  /** Почему полотно пусто. Пусто — есть что рисовать. */
  empty: string;
}

/**
 * Наши ордера окна: настоящие и пробные.
 *
 * Сырыми строками, а не готовыми точками: куда поставить точку, решает шкала
 * полотна цены, а она считается здесь же, во вьюмодели.
 */
export interface TimelineOrders {
  real: readonly Order[];
  paper: readonly PaperOrder[];
}

export const NO_ORDERS: TimelineOrders = { real: [], paper: [] };

/** Точка ордера на полотне цены. */
export interface TimelineMark {
  key: string;
  /** Координаты в единицах viewBox. По вертикали они же экранные пиксели:
   *  высота полотна задана в тех же единицах, что его viewBox. */
  x: number;
  y: number;
  /** Цвет исхода — того же, что у линии его стакана. */
  color: string;
  /** Пробный: рисуется кольцом, настоящий — сплошной точкой. */
  paper: boolean;
  /** Ордер закрыт и ничего не купил — бледнее. */
  dim: boolean;
  /** Первая строка подсказки: сторона, исход, настоящий или пробный. */
  title: string;
  /** Остальные строки подсказки. */
  lines: string[];
}

/**
 * Радиус попадания курсора в точку ордера, экранные пиксели.
 *
 * Больше самой точки (её радиус — четыре с половиной): в кружок в девять
 * пикселей на живом графике с первого раза не попасть, а промах здесь стоит
 * только подсказки, которой не видно.
 */
export const MARK_HIT_PX = 7;

export interface TimelineView {
  /** Есть ли вообще ответ. false — на экране одна причина словами. */
  known: boolean;
  /**
   * Длина оси в секундах. Нужна разметке, чтобы перевести пиксель под курсором
   * в секунду окна: холст тянется по ширине карточки, и пересчёт возможен
   * только там, где известна его фактическая ширина.
   */
  span: number;
  /** Левый край оси в секундах от начала окна: отрицательный — предстарт на оси. */
  from: number;
  /**
   * Координата открытия окна в единицах viewBox. null — предстарта на оси нет,
   * и открытие совпадает с левым краем.
   *
   * Отдельной отметкой, а не одним из штрихов сетки: слева от неё котировка
   * живёт ещё без страйка, справа — со страйком, и это два разных рынка на
   * одной линии. Не отметь границу — и скачок стакана на открытии читался бы
   * как событие внутри окна.
   */
  openAt: number | null;
  /** Почему графика нет целиком. */
  empty: string;
  /** Основания ПЕРЕД числами: чем считано, по скольким строкам, как давно. */
  basis: string[];
  /** Предупреждения: обрезка выборки, неполнота окна, отсутствие котировки. */
  alarms: string[];
  price: TimelineChart;
  /** Индекс: опубликованный S, наш отчёт t−3, достройка и страйк (прогноз до открытия). */
  index: TimelineChart;
  /** Отклонение S от страйка и запас — в долларах вокруг нулевой черты. */
  gap: TimelineChart;
  edge: TimelineChart;
  /** Ставки за корзину — когда пришла и какого объёма. */
  flow: TimelineChart;
  /**
   * Координата нуля на полотне накопления.
   *
   * Оставлена ради совместимости разметки; у каждого полотна теперь свой
   * `zeroAt` внутри самого чарта — у оборота он по низу, у накопления посередине
   * шкалы, и держать это снаружи значило бы, что разметка знает смысл полотен.
   */
  zeroAt: number;
  /** Деления оси времени, общие для обоих полотен. */
  xTicks: TimelineTick[];
  /** Строка «что первично» и её оговорка. */
  lead: string;
  leadHint: string;
  /** Подпись «до открытия окна». Пусто — досделочного накопления не было. */
  beforeOpen: string;
  /** Чей это состав — одной строкой в шапку карточки. */
  picked: string;
}

const EMPTY_CHART: TimelineChart = { lines: [], bars: [], ticks: [], empty: "" };

const EMPTY_VIEW: TimelineView = {
  known: false,
  span: 1,
  from: 0,
  openAt: null,
  empty: "",
  basis: [],
  alarms: [],
  price: EMPTY_CHART,
  index: EMPTY_CHART,
  gap: EMPTY_CHART,
  edge: EMPTY_CHART,
  flow: EMPTY_CHART,
  zeroAt: EDGE_H / 2,
  xTicks: [],
  lead: "",
  leadHint: "",
  beforeOpen: "",
  picked: "",
};

/** Имя исхода по asset_id из справочника рынка. */
export type OutcomeNames = Readonly<Record<string, string>>;

/**
 * Вьюмодель панели.
 *
 * Одна функция на обе картинки, а не две: ось времени у них ОБЩАЯ, и посчитай
 * её дважды — полотна разъехались бы на любом расхождении входов, а разъезд
 * такой не виден глазом и превращает панель в генератор ложных выводов о
 * первичности.
 */
export function timelineView(
  t: Timeline | undefined,
  outcomes: OutcomeNames,
  orders: TimelineOrders = NO_ORDERS,
): TimelineView {
  if (!t) return EMPTY_VIEW;
  // Почему нет накопления. Раньше это означало «графика нет целиком», но ряд
  // индекса от сделок не зависит, а предстарт окна обычно проходит вовсе без
  // них — именно там, где прогноз страйка объясняет, почему поехал стакан.
  // Поэтому при живом ряде индекса причина уходит в полотна накопления, а не
  // гасит панель.
  const accEmpty = !t.known
    ? t.reason || "ряд не посчитан: причина не названа"
    : t.points.length === 0
      ? t.reason || "в окне ещё нет сделок"
      : "";
  if (accEmpty !== "" && (t.index?.length ?? 0) === 0) {
    return { ...EMPTY_VIEW, known: t.known, empty: accEmpty };
  }

  const from = axisFrom(t);
  const span = axisSpan(t);
  const x = (sec: number) => ((sec - from) / span) * PLOT_W;

  return {
    known: true,
    span,
    from,
    openAt: from < 0 ? x(0) : null,
    empty: "",
    basis: basisOf(t),
    alarms: alarmsOf(t),
    price: priceChart(t, outcomes, x, placeOrders(t, outcomes, orders, from, span)),
    index: indexChart(t, x),
    gap: gapChart(t, x),
    edge: accEmpty !== "" ? emptyChart(accEmpty) : netChart(t, outcomes, x),
    flow: accEmpty !== "" ? emptyChart(accEmpty) : flowChart(t, outcomes, span, x),
    zeroAt: zeroAt(t.points, !t.pick_reason),
    xTicks: xTicks(from, span, x),
    ...leadOf(t),
    beforeOpen: beforeOpenNote(t),
    picked: pickedNote(t),
  };
}

/**
 * Основания — ПЕРЕД числами и всегда, а не только когда что-то не так.
 *
 * Первым идёт знаменатель: «линия ровная» на трёх сделках и на трёх тысячах —
 * разные утверждения, и по картинке они неразличимы.
 */
function basisOf(t: Timeline): string[] {
  const out: string[] = [];
  out.push(
    `по ${t.trades} ${plural(t.trades, "сделке", "сделкам", "сделкам")} ` +
      `${t.wallets} ${plural(t.wallets, "кошелька", "кошельков", "кошельков")}`,
  );
  out.push(`шаг сетки ${t.step_s} ${plural(t.step_s, "секунда", "секунды", "секунд")}`);
  if (t.groups?.length || t.notgroups?.length) {
    // Два окна на одном экране: подгруппа назначена за всю глубину, накопление
    // считано за это окно. Молчание об этом — главный способ соврать панелью.
    out.push("подгруппа назначена за всю накопленную глубину, накопление — за это окно");
    // Возраст раздачи печатается ВСЕГДА, включая ноль, а не только когда он
    // ненулевой. Ноль — законное значение («пересчитали только что»), а
    // появляющийся и исчезающий кусок строки менял бы её длину раз в минуту и
    // на переносе двигал бы карточку по высоте.
    out.push(`раздаче по подгруппам ${Math.round((t.groups_age_ms ?? 0) / 1000)} с`);
  }
  return out;
}

function alarmsOf(t: Timeline): string[] {
  const out: string[] = [];
  if (t.truncated) {
    out.push("выборка обрезана потолком источника: ряд ниже настоящего, а не «менее точен»");
  }
  if (t.partial) {
    out.push("окно наблюдается не с начала: левый край линии обрывается не потому, что там тихо");
  }
  if (t.groups_reason) out.push(t.groups_reason);
  if (t.pick_reason) out.push(t.pick_reason);
  if (t.quotes_reason) out.push(t.quotes_reason);
  return out;
}

/**
 * Полотно цены: середина стакана по каждому исходу.
 *
 * Именно середина, а не цена принтов. Линия из принтов частично СДЕЛАНА той
 * группой, чьё накопление рисуется рядом, и вопрос «кто первичен» получил бы
 * тавтологичный ответ.
 *
 * Шкала — по НАБЛЮДЁННОМУ размаху с полом PRICE_MIN_SPAN, а не жёсткие 0…1.
 * Жёсткая шкала на рынке, весь день стоящем в 0.45–0.55, показывала бы две
 * прямые посередине; пол не даёт обратного — увеличения дрожания на один тик до
 * высоты полотна.
 */
function priceChart(
  t: Timeline,
  outcomes: OutcomeNames,
  x: (s: number) => number,
  placed: PlacedOrders,
): TimelineChart {
  const up = midSeries(t.quotes, (q) => [q.up_bid, q.up_ask]);
  const down = midSeries(t.quotes, (q) => [q.down_bid, q.down_ask]);
  const showPick = !t.pick_reason;
  const marketAvg = t.points.map((p) => price(p.all_avg));
  const groupAvg = showPick ? t.points.map((p) => price(p.avg)) : [];

  const values = [
    ...[...up, ...down].filter((p): p is QuotePoint => p !== null).map((p) => p.v),
    ...[...marketAvg, ...groupAvg].filter((v): v is number => v !== null),
  ];
  if (values.length === 0) {
    return {
      lines: [],
      bars: [],
      ticks: [],
      empty: t.quotes_reason || "ряда котировки по этому окну нет",
    };
  }

  // Цены ордеров входят в шкалу: точка за краем полотна — это ордер, которого
  // на графике нет, а ставят их как раз у краёв — по 0.99 на решённое окно.
  for (const o of placed.orders) values.push(o.price);
  const { lo, hi } = paddedRange(values, PRICE_MIN_SPAN);
  const y = (v: number) => PRICE_H - ((v - lo) / (hi - lo)) * PRICE_H;

  const lines = [
    quoteLine("up", `${firstName(t, outcomes)}, стакан`, up, "var(--color-up)", x, y),
    quoteLine("down", `${secondName(t, outcomes)}, стакан`, down, "var(--color-down)", x, y),
    // Средняя цена ВХОДА — на той же шкале, что и стакан, и это вся суть
    // полотна: видно, дороже или дешевле нынешней котировки набирала сторона.
    // Приведена к оси первого исхода, поэтому сравнима именно с его ценой.
    line("market-avg", "весь рынок, средняя входа", marketAvg, t.points,
      "var(--color-muted)", true, x, y, priceLabel),
  ];
  if (showPick) {
    lines.push(
      line("group-avg", "отобранные, средняя входа", groupAvg, t.points,
        "var(--color-accent)", true, x, y, priceLabel),
    );
  }
  return {
    lines: [...lines.filter((l) => l.paths.length > 0), ...markLegend(placed)],
    bars: [],
    // Пробные — первыми, настоящие поверх: там, где они легли в одну точку,
    // наверху обязан быть ордер, который тратил деньги.
    marks: [...placed.orders]
      .sort((a, b) => Number(b.mark.paper) - Number(a.mark.paper) || a.sec - b.sec)
      .map((o) => ({ ...o.mark, x: x(o.sec), y: y(o.price) })),
    ticks: valueTicks(lo, hi, y, priceLabel),
    empty: "",
  };
}

/** Ордер, разложенный на ось: секунда окна, цена и всё, кроме координат. */
interface PlacedOrder {
  sec: number;
  price: number;
  mark: Omit<TimelineMark, "x" | "y">;
}

interface PlacedOrders {
  orders: PlacedOrder[];
  /** Ордера, которые на ось не попали, — по видам. Называются в легенде. */
  outside: { real: number; paper: number };
}

/**
 * Ордера окна, разложенные по оси времени и шкале цены.
 *
 * # Почему точка стоит на ЛИМИТЕ, а не на цене исполнения
 *
 * Потому что цены исполнения у настоящего ордера нет: биржа отдаёт по нему
 * только лимит и исполненный размер (см. amountHint в таблице ордеров). Поставь
 * пробу на её среднюю налива, а настоящий ордер на лимит — и две точки одной
 * кнопки легли бы по-разному по причине, которой на графике не видно. Средняя
 * налива пробы стоит в подсказке.
 *
 * # Почему ордер за краем оси не прижимается к краю
 *
 * Прижатая точка назвала бы секунду, в которую ордера не было. Вместо неё число
 * таких ордеров стоит в легенде: молча выпавший ордер читался бы как «не
 * ставил».
 */
function placeOrders(
  t: Timeline,
  outcomes: OutcomeNames,
  orders: TimelineOrders,
  from: number,
  span: number,
): PlacedOrders {
  const out: PlacedOrders = { orders: [], outside: { real: 0, paper: 0 } };
  const start = t.window_start ? Date.parse(t.window_start) : NaN;
  // Без начала окна секунду ордера отсчитать не от чего: ось тогда идёт от
  // первой точки ряда, а не от часов.
  if (!Number.isFinite(start)) return out;

  const place = (at: string, rawPrice: string, kind: "real" | "paper", mark: () => PlacedOrder["mark"]) => {
    const ms = Date.parse(at);
    const p = parse(rawPrice);
    if (!Number.isFinite(ms) || p === null) return;
    const sec = (ms - start) / 1000;
    if (sec < from || sec > from + span) {
      out.outside[kind]++;
      return;
    }
    out.orders.push({ sec, price: p / SCALE, mark: mark() });
  };

  for (const o of orders.real) {
    place(o.created_at, o.price, "real", () => realMark(t, outcomes, o, start));
  }
  for (const o of orders.paper) {
    place(o.placed_at, o.limit_price, "paper", () => paperMark(t, outcomes, o, start));
  }
  return out;
}

function realMark(
  t: Timeline,
  outcomes: OutcomeNames,
  o: Order,
  start: number,
): PlacedOrder["mark"] {
  const matched = parse(o.size_matched) ?? 0;
  const terminal = isTerminal(o.status);
  const lines = [
    whenLine(o.created_at, start),
    `${o.order_type} · лимит ${o.price} × ${o.size} share = $${notional(o.price, o.size) ?? "—"}`,
    matched > 0
      ? `исполнено ${o.size_matched} из ${o.size} share`
      : terminal
        ? "не исполнен"
        : "ещё не исполнен",
    `статус ${o.status}`,
  ];
  // Срок нужен только живому GTD: у закрытого он уже ничего не объясняет.
  if (o.expires_at && !terminal) lines.push(`срок до ${formatTime(o.expires_at)}`);
  if (o.reason) lines.push(o.reason);
  return {
    key: `real-${o.client_order_id}`,
    color: outcomeColor(t, o.asset_id),
    paper: false,
    dim: terminal && matched === 0,
    title: `${o.side} ${outcomeName(t, outcomes, o.asset_id)} · настоящий${issuerNote(o.issued_by)}`,
    lines,
  };
}

const PAPER_STATUS: Record<PaperStatus, string> = {
  filled: "налит",
  partial: "налит частично",
  rejected: "отказ",
};

function paperMark(
  t: Timeline,
  outcomes: OutcomeNames,
  o: PaperOrder,
  start: number,
): PlacedOrder["mark"] {
  const filled = parse(o.filled_size) ?? 0;
  const quote = parse(o.quote);
  // Средняя налива — деньги по задетым уровням на налитый размер. От лимита
  // она отличается ровно на проскальзывание, ради которого проба и заведена.
  const avg = filled > 0 && quote !== null ? div(quote, filled) : null;
  const effective = parse(o.effective_price);
  const lines = [
    whenLine(o.placed_at, start),
    `лимит ${o.limit_price} × ${o.requested_size} share`,
    filled > 0
      ? `налито ${o.filled_size} share по средней ${avg === null ? "—" : roundToFixed(avg, 4)}` +
        ` · уровней ${o.levels}`
      : "не налито",
  ];
  if (effective !== null) lines.push(`цена входа с комиссией ${roundToFixed(effective, 4)}`);
  else if (filled > 0 && o.fee === null) lines.push("комиссия рынка неизвестна");
  lines.push(
    `стакан bid ${o.best_bid ?? "—"} / ask ${o.best_ask ?? "—"}` +
      (o.book_age_ms === null ? "" : `, возраст ${o.book_age_ms} мс`),
  );
  lines.push(`статус ${PAPER_STATUS[o.status] ?? o.status}`);
  if (o.kill_switch) lines.push("торговля стояла на стопе: настоящий ордер в эту секунду не ушёл бы");
  if (o.reason) lines.push(o.reason);
  return {
    key: `paper-${o.id}`,
    color: outcomeColor(t, o.asset_id),
    paper: true,
    dim: filled === 0,
    title: `${o.side} ${outcomeName(t, outcomes, o.asset_id)} · пробный${issuerNote(o.issued_by)}`,
    lines,
  };
}

/** «+1:23 окна · 12:34:56»: секунда оси и часы — их сверяют с таблицей ордеров. */
function whenLine(at: string, start: number): string {
  return `${clock(Math.round((Date.parse(at) - start) / 1000))} окна · ${formatTime(at)}`;
}

/** Кем выставлен — только когда не оператором, как в таблице ордеров. */
function issuerNote(issuedBy: string): string {
  return issuedBy && issuedBy !== "operator" ? ` · ${issuerLabel(issuedBy)}` : "";
}

/** Цвет исхода — тот же, что у линии его стакана: точка читается против неё. */
function outcomeColor(t: Timeline, assetId: string): string {
  if (assetId === t.first_asset_id) return "var(--color-up)";
  if (assetId === t.second_asset_id) return "var(--color-down)";
  return "var(--color-muted)";
}

function outcomeName(t: Timeline, outcomes: OutcomeNames, assetId: string): string {
  if (assetId === t.first_asset_id) return firstName(t, outcomes);
  if (assetId === t.second_asset_id) return secondName(t, outcomes);
  return outcomes[assetId] ?? "исход не из справочника";
}

/**
 * Строки легенды под точки ордеров — по одной на вид, только когда он есть.
 *
 * Число — сколько точек на полотне; ордера за краем оси названы рядом, а не
 * выброшены молча.
 */
function markLegend(placed: PlacedOrders): TimelineLine[] {
  const count = (paper: boolean) => placed.orders.filter((o) => o.mark.paper === paper).length;
  const value = (shown: number, outside: number) =>
    outside > 0 ? `${shown}, ещё ${outside} вне оси` : String(shown);
  const out: TimelineLine[] = [];
  const real = count(false);
  if (real > 0 || placed.outside.real > 0) {
    out.push({
      key: "orders-real",
      label: "мои ордера",
      paths: [],
      color: "var(--color-fg)",
      faint: false,
      value: value(real, placed.outside.real),
      swatch: "dot",
    });
  }
  const paper = count(true);
  if (paper > 0 || placed.outside.paper > 0) {
    out.push({
      key: "orders-paper",
      label: "пробные",
      paths: [],
      color: "var(--color-fg)",
      faint: false,
      value: value(paper, placed.outside.paper),
      swatch: "ring",
    });
  }
  return out;
}

/**
 * Точки ордеров под курсором, ближайшая первой.
 *
 * Попадание считается по РАССТОЯНИЮ, а не наведением на сам кружок: точки одной
 * секунды лежат друг на друге, и наведение на верхнюю прятало бы нижние — а
 * стратегия ставит ордера пачкой именно так. Под курсором оказываются все.
 *
 * По горизонтали единицы viewBox переводятся в пиксели фактической шириной
 * холста — он тянется по карточке; по вертикали они и есть пиксели.
 */
export function marksNear(
  marks: readonly TimelineMark[],
  cx: number,
  cy: number,
  widthPx: number,
): TimelineMark[] {
  if (widthPx <= 0) return [];
  return marks
    .map((m) => ({ m, d: Math.hypot((m.x / PLOT_W) * widthPx - cx, m.y - cy) }))
    .filter((e) => e.d <= MARK_HIT_PX)
    .sort((a, b) => a.d - b.d)
    .map((e) => e.m);
}

/**
 * Полотно накопления: сколько набрано и в какую сторону.
 *
 * # Почему ШЕРЫ, а не доллары
 *
 * Потому что это ответ на вопрос «в какую сторону и сколько», и он же стоит в
 * строке «Итого» над таблицей накопления. Шеры — то, что сторона получит на
 * погашении; доллары — то, что она за это отдала. На дешёвой стороне числа
 * расходятся на порядок: тысяча шеров Down по 0.05 — это $50, и подписать
 * «набрано $50» под позицией, которая принесёт $1000, значит соврать о размере
 * ставки. Доллары показывает курсор.
 *
 * # Почему линии «весь рынок» здесь НЕТ
 *
 * Потому что она тождественно ноль, и это не свойство данных, а устройство
 * рынка: шер Up existует только в паре с шером Down — комплект чеканится из
 * доллара и разрезается. Значит Σ(нетто Up) − Σ(нетто Down) по ВСЕМ кошелькам
 * равна нулю всегда. Замер 2026-09-05 на восьми живых окнах: 0, 0, 0.02, 0, 0,
 * −21.31 при обороте в тысячи шеров, 0, 0 — расхождения на неполных парах.
 *
 * Ровная нулевая линия «весь рынок» не отвечала бы ни на один вопрос, а
 * выглядела бы как измерение — худший вид неправды на этой панели. Её роль
 * играет сама нулевая черта: у выбранной группы +500, значит у ВСЕХ ОСТАЛЬНЫХ
 * ровно −500, и вторая линия была бы зеркалом первой.
 *
 * В долларах ноля нет (чеканка платит p за Up и 1−p за Down), но и позицией то
 * число не является — оно про перекос цен потока. Поэтому доллары живут в
 * подсказке курсора, а не отдельной линией.
 */
function netChart(t: Timeline, outcomes: OutcomeNames, x: (s: number) => number): TimelineChart {
  const { lo, hi } = netRange(t.points, !t.pick_reason);
  const y = (v: number) => EDGE_H - ((v - lo) / (hi - lo)) * EDGE_H;
  const at = (get: (p: TimelinePoint) => string | undefined) =>
    t.points.map((p) => shares(get(p)));

  // Без фильтра — разрез ПО РОЛИ, а не по исходу × роль.
  //
  // Итог по рынку тождественно ноль, но составляющие — нет, и разрез рисуется.
  // Роль, а не исход, потому что по рынку целиком разрез по исходу упирается в
  // чеканку: комплект режется из доллара, покупка Up идёт в паре с покупкой
  // Down. Замер на живом окне: составляющие по исходу дают ±37…40 тысяч шеров, а
  // весь смысл — в разнице между ними, около тысячи. Четыре линии такого
  // масштаба показали бы объём чеканки, спрятав сигнал в толщину линии.
  //
  // По роли этого нет: тейкеры −1089, мейкеры +1100. Читается прямо —
  // агрессивные деньги давят в одну сторону, пассивные принимают.
  if (t.pick_reason) {
    const lines = [
      line("all-taker", "тейкеры рынка", at((p) => p.all_taker), t.points,
        "var(--color-accent)", false, x, y, sharesLabel),
      line("all-maker", "мейкеры рынка", at((p) => p.all_maker), t.points,
        "var(--color-accent)", true, x, y, sharesLabel),
    ].filter((l) => l.paths.length > 0);
    if (lines.length === 0) {
      return { lines: [], bars: [], ticks: [], empty: t.pick_reason };
    }
    return {
      lines,
      bars: [],
      zeroAt: y(0),
      ticks: valueTicks(lo, hi, y, sharesLabel),
      empty: "",
    };
  }

  // Порядок: сначала составляющие, итог ПОСЛЕДНИМ — он рисуется поверх них.
  // Цвет несёт исход, штрих — роль: два признака на четырёх линиях читаются без
  // возврата к легенде, а четыре разных цвета в палитре взять негде.
  const lines = [
    line("up-taker", `${firstName(t, outcomes)} тейкером`, at((p) => p.up_taker),
      t.points, "var(--color-up)", false, x, y, sharesLabel),
    line("up-maker", `${firstName(t, outcomes)} мейкером`, at((p) => p.up_maker),
      t.points, "var(--color-up)", true, x, y, sharesLabel),
    line("down-taker", `${secondName(t, outcomes)} тейкером`, at((p) => p.down_taker),
      t.points, "var(--color-down)", false, x, y, sharesLabel),
    line("down-maker", `${secondName(t, outcomes)} мейкером`, at((p) => p.down_maker),
      t.points, "var(--color-down)", true, x, y, sharesLabel),
    line("group", "итого", at((p) => p.net), t.points,
      "var(--color-accent)", false, x, y, sharesLabel),
  ];
  return {
    lines: lines.filter((l) => l.paths.length > 0),
    bars: [],
    zeroAt: y(0),
    ticks: valueTicks(lo, hi, y, sharesLabel),
    empty: "",
  };
}

/**
 * Шкала накопления. Ноль в неё входит ВСЕГДА: перевес — величина со знаком, и
 * шкала без нуля показывала бы переход на другую сторону обычным наклоном.
 *
 * Отдельной функцией, потому что её зовут дважды — линии и координата нуля, — а
 * две копии одной шкалы разъехались бы, и нулевая черта встала бы не там, где
 * линии её пересекают.
 */
function netRange(points: TimelinePoint[], picked: boolean): { lo: number; hi: number } {
  const values: number[] = [0];
  for (const p of points) {
    // Шкала вмещает ВСЕ линии своего разреза: обрежь её по итогу — и линия
    // «Down тейкером» ушла бы за край именно там, где она интереснее всего. У
    // рынка разрез другой, и мерить его шкалой отбора нечем: полей отбора там
    // нет вовсе.
    const raws = picked
      ? [p.net, p.up_taker, p.up_maker, p.down_taker, p.down_maker]
      : [p.all_taker, p.all_maker];
    for (const raw of raws) {
      const v = shares(raw);
      if (v !== null) values.push(v);
    }
  }
  return paddedRange(values, 1);
}

/**
 * Полотно ставок: сколько пришло ЗА КОРЗИНУ и в какую сторону.
 *
 * # Зачем оно отдельно от накопления
 *
 * Потому что величины несоизмеримы: накопленное — тысячи шеров, поток за
 * секунду — десятки. Нарисуй их на одной шкале, и столбики легли бы в линию у
 * нуля именно там, где интересны. Своё полотно со своей шкалой отвечает на свой
 * вопрос — «когда пришла ставка и какого объёма», — а общая ось времени держит
 * его в одной вертикали с накоплением: всплеск виден напротив своего излома.
 *
 * Тот же довод и то же устройство, что у полотна цены: одна ось X, свои шкалы Y,
 * подписи у каждого полотна.
 *
 * # Почему стороны РАЗНЫМИ столбиками, а не одним со знаком
 *
 * Одна встречная пара в ту же секунду погасила бы обе, и крупная ставка исчезла
 * бы с графика ровно в тот момент, ради которого он и заведён. Стороны растут от
 * нуля в разные стороны, шкала симметрична — высоты сравнимы напрямую.
 *
 * # Почему шкала по МАКСИМУМУ, а не по среднему
 *
 * Вопрос полотна — «где крупная ставка», и крупная обязана быть выше всех. Шкала
 * по среднему сплющила бы её к потолку вместе с соседями, то есть спрятала бы
 * ровно тот столбик, ради которого смотрят.
 */
function flowChart(
  t: Timeline,
  outcomes: OutcomeNames,
  span: number,
  x: (s: number) => number,
): TimelineChart {
  const step = Math.max(t.step_s, 1);
  // Ширина столбика — ровно корзина, минус волос на просвет: слипшиеся столбики
  // читались бы как заливка, и «отрезок без ставок» перестал бы быть виден.
  const w = Math.max((step / span) * PLOT_W - 0.4, 0.6);
  const picked = !t.pick_reason;

  const peak = Math.max(
    ...t.points.flatMap((p) =>
      (picked ? [shares(p.in_up), shares(p.in_down)] : [shares(p.all_flow)]).map((v) =>
        v === null ? 0 : v,
      ),
    ),
    0,
  );
  if (peak <= 0) {
    return {
      lines: [],
      bars: [],
      ticks: [],
      empty: picked
        ? "ставок отобранных кошельков в этом окне ещё не было"
        : "в этом окне ещё не торговали",
    };
  }

  const bars: TimelineBar[] = [];
  if (picked) {
    // Отобранный состав НЕ гасит сам себя: обе стороны считаются порознь и
    // растут от середины в разные стороны, шкала симметрична.
    const half = FLOW_H / 2;
    for (const p of t.points) {
      const at = x(p.t);
      const up = shares(p.in_up);
      if (up !== null && up > 0) {
        const h = (up / peak) * half;
        bars.push({ key: `u${p.t}`, x: at, w, y: half - h, h, color: "var(--color-up)" });
      }
      const down = shares(p.in_down);
      if (down !== null && down > 0) {
        const h = (down / peak) * half;
        bars.push({ key: `d${p.t}`, x: at, w, y: half, h, color: "var(--color-down)" });
      }
    }
    return {
      lines: [
        barLegend("in-up", `${firstName(t, outcomes)} за корзину`, "var(--color-up)"),
        barLegend("in-down", `${secondName(t, outcomes)} за корзину`, "var(--color-down)"),
        barLegend("top", "крупнейшая одиночная", "var(--color-warn)"),
      ],
      bars,
      zeroAt: half,
      ticks: [
        { at: 0, label: sharesLabel(peak), anchor: "end" },
        { at: half, label: "0", anchor: "end" },
        { at: FLOW_H, label: sharesLabel(peak), anchor: "end" },
      ],
      empty: "",
    };
  }

  // Без фильтра — ОБОРОТ, односторонним столбиком от низа и нейтральным цветом.
  //
  // Направления тут нет, и это не упрощение: в журнале лежат ОБЕ ноги каждой
  // сделки, по рынку целиком стороны гасят друг друга, а остаточный перекос
  // секунды — артефакт границы (у части транзакций ноги попадают в разные
  // секунды). Нарисуй его двумя цветными столбиками — и панель называла бы
  // направлением разъезд меток времени.
  for (const p of t.points) {
    const v = shares(p.all_flow);
    if (v === null || v <= 0) continue;
    const h = (v / peak) * FLOW_H;
    bars.push({
      key: `f${p.t}`,
      x: x(p.t),
      w,
      y: FLOW_H - h,
      h,
      color: "var(--color-muted)",
    });
  }
  return {
    lines: [
      barLegend("all-flow", "оборот за корзину, обе ноги", "var(--color-muted)"),
      barLegend("all-top", "крупнейшее одиночное исполнение", "var(--color-warn)"),
    ],
    bars,
    zeroAt: FLOW_H,
    ticks: [
      { at: 0, label: sharesLabel(peak), anchor: "end" },
      { at: FLOW_H, label: "0", anchor: "end" },
    ],
    empty: "",
  };
}

/** Полотно без линий и с причиной словами. */
function emptyChart(reason: string): TimelineChart {
  return { lines: [], bars: [], ticks: [], empty: reason };
}

/**
 * Полотно индекса: что в каждую секунду показывала карточка рынка.
 *
 * # Почему линии по моменту ЗАПИСИ, а не по меткам отчётов
 *
 * Отчёт t−3 и опубликованное значение — одна и та же метрика. Положенные по
 * меткам источника, они совпали бы, и вся фора исчезла бы с графика. Положенные
 * по моменту, когда их знала карточка, они идут со сдвигом, и этот сдвиг и есть
 * то, на сколько мы раньше провода.
 *
 * # Почему прогноз страйка и страйк — РАЗНЫЕ линии
 *
 * До открытия страйка ещё нет, а есть прогноз, который двигается; после — число,
 * которое не изменится. Одна линия через открытие читалась бы как страйк,
 * который продолжает ездить. Тот же довод, что у крышки над буквой на карточке.
 *
 * # Зачем биржа и сырой ряд рядом с S
 *
 * Стакан прыгает в секунду рывка биржи, а S — среднее сырого ряда за минуту и
 * доносит тот же рывок наклоном на полминуты позже. Без этих двух линий скачок
 * цены стоит на полотне без причины. Биржа котирована в USDT и сдвинута ОДНИМ
 * числом на окно (index_spot_basis): сдвиг своей секунды нарисовал бы дрожание
 * базиса как ход рынка. Поэтому уровень биржи против страйка верен с точностью
 * до дрейфа базиса за окно, а ход — точно.
 */
function indexChart(t: Timeline, x: (s: number) => number): TimelineChart {
  const pts = t.index ?? [];
  if (pts.length === 0) return emptyChart(t.index_reason || "ряда индекса по этому окну нет");
  const step = Math.max(t.step_s, 1);

  const s = indexSeries(pts, (p) => p.index);
  const ahead = indexSeries(pts, (p) => p.ahead);
  const fwd = indexSeries(pts, (p) => p.fwd);
  const kHat = indexSeries(pts, (p) => (p.strike_forecast ? p.strike : undefined));
  const k = indexSeries(pts, (p) => (p.strike_forecast ? undefined : p.strike));
  const raw = indexSeries(pts, (p) => p.raw);
  const basis = parse(t.index_spot_basis);
  // Без базиса линии биржи нет вовсе: USDT-цена на шкале индекса как есть стояла
  // бы на шестьдесят долларов выше правды и читалась бы как уход от страйка.
  const spot = basis === null ? [] : indexSeries(pts, (p) => shiftedPrice(p.spot, basis));

  const values = [...s, ...ahead, ...fwd, ...kHat, ...k, ...raw, ...spot]
    .filter((p): p is ValuePoint => p !== null)
    .map((p) => p.v);
  const level = values[0] ?? 0;
  const { lo, hi } = paddedRange(values, Math.max(Math.abs(level) * INDEX_MIN_SPAN_SHARE, 0.01));
  const y = (v: number) => INDEX_H - ((v - lo) / (hi - lo)) * INDEX_H;
  const fmt = (raw: string) => formatIndex(raw);

  const lines = [
    // Страйк — первым, под индексом: он опора, против которой читают S.
    valueLine("strike-forecast", "K̂ прогноз страйка", kHat, "var(--color-warn)", true, step, x, y, fmt),
    valueLine("strike", "K страйк", k, "var(--color-warn)", false, step, x, y, fmt),
    valueLine("index", "S", s, "var(--color-fg)", false, step, x, y, fmt),
    valueLine("ahead", `Ŝ t−${REAL_DATA_LAG_S}`, ahead, "var(--color-accent)", false, step, x, y, fmt),
    valueLine("fwd", `Ŝ ${fwdLagLabel(pts)}`, fwd, "var(--color-accent)", true, step, x, y, fmt),
    // Сырой ряд и биржа — последними, поверх: ступенька, от которой прыгает
    // стакан, не должна прятаться под гладкими линиями.
    valueLine("raw", "Chainlink сырой", raw, "var(--color-muted)", false, step, x, y, fmt),
    valueLine("spot", `Binance, сдвиг ${signedUsd(t.index_spot_basis)}`, spot, "var(--color-spot)", false,
      step, x, y, fmt),
  ];
  return {
    lines: lines.filter((l) => l.paths.length > 0),
    bars: [],
    ticks: valueTicks(lo, hi, y, (v) => dollarsTick(v, hi - lo, false)),
    empty: "",
  };
}

/**
 * Полотно отклонения и запаса.
 *
 * # Почему на одном полотне, вопреки доводу «несоизмеримое — порознь»
 *
 * Потому что здесь соизмеримо: обе величины в долларах и обе отсчитаны от
 * страйка. Отклонение говорит, где индекс СЕЙЧАС, запас — где кончится окно, если
 * цена замрёт; к концу окна они сходятся, а расхождение между ними и есть снос,
 * уже заложенный в метрику. Разнеси их — и это сравнение пришлось бы делать
 * глазом через полотно.
 *
 * До начала окна страйка запас лежит ровно на нуле, и это не пропуск: ход цены
 * сдвигает страйк и итог на одну и ту же величину, и стороны у окна ещё нет.
 */
function gapChart(t: Timeline, x: (s: number) => number): TimelineChart {
  const pts = t.index ?? [];
  if (pts.length === 0) return emptyChart(t.index_reason || "ряда индекса по этому окну нет");
  const step = Math.max(t.step_s, 1);

  const dev = indexSeries(pts, (p) => p.deviation);
  const cushion = indexSeries(pts, (p) => p.cushion);
  const cushionAhead = indexSeries(pts, (p) => p.cushion_ahead);
  const values = [...dev, ...cushion, ...cushionAhead]
    .filter((p): p is ValuePoint => p !== null)
    .map((p) => p.v);
  if (values.length === 0) {
    return emptyChart("страйка нет — ни прогноза, ни записанного: отклонение и запас считать не от чего");
  }

  const level = parse(pts[pts.length - 1]?.index);
  const minSpan = Math.max(((level ?? 0) / SCALE) * GAP_MIN_SPAN_SHARE, 0.01);
  // Ноль в шкалу входит всегда: величины со знаком, и шкала без нуля показала
  // бы переход на другую сторону страйка обычным наклоном.
  const { lo, hi } = paddedRange([0, ...values], minSpan);
  const y = (v: number) => GAP_H - ((v - lo) / (hi - lo)) * GAP_H;

  const lines = [
    valueLine("deviation", "отклонение S − K", dev, "var(--color-fg)", false, step, x, y, signedUsd),
    valueLine("cushion", "запас", cushion, "var(--color-accent)", false, step, x, y, signedUsd),
    valueLine("cushion-ahead", "запас по достройке", cushionAhead, "var(--color-accent)", true,
      step, x, y, signedUsd),
  ];
  return {
    lines: lines.filter((l) => l.paths.length > 0),
    bars: [],
    zeroAt: y(0),
    ticks: valueTicks(lo, hi, y, (v) => dollarsTick(v, hi - lo, true)),
    empty: "",
  };
}

/** Цена, сдвинутая на базис, строкой ответа; нет цены — нет и точки. */
function shiftedPrice(raw: string | undefined, basis: Micro): string | undefined {
  const m = parse(raw);
  return m === null ? undefined : roundToFixed(m + basis, 6);
}

/** Точка ряда индекса: число для шкалы и строка ответа для подписи. */
interface ValuePoint {
  t: number;
  v: number;
  raw: string;
}

function indexSeries(
  pts: TimelineIndexPoint[],
  get: (p: TimelineIndexPoint) => string | undefined,
): (ValuePoint | null)[] {
  return pts.map((p) => {
    const raw = get(p);
    const m = parse(raw);
    return raw === undefined || m === null ? null : { t: p.t, v: m / SCALE, raw };
  });
}

/**
 * Линия ряда индекса.
 *
 * Рвётся на пропуске значения И на дыре во времени шире допуска: у ряда индекса
 * корзины без записи просто нет в ответе, и без проверки времени линия молча
 * соединила бы минуту молчания публикатора прямым отрезком.
 */
function valueLine(
  key: string,
  label: string,
  series: (ValuePoint | null)[],
  color: string,
  faint: boolean,
  step: number,
  x: (s: number) => number,
  y: (v: number) => number,
  fmt: (raw: string) => string,
): TimelineLine {
  const paths: string[] = [];
  let cur: string[] = [];
  let prevT: number | null = null;
  for (const p of series) {
    if (p === null || (prevT !== null && p.t - prevT > step + INDEX_GAP_TOLERANCE_S)) {
      if (cur.length > 1) paths.push(cur.join(" "));
      cur = [];
    }
    if (p === null) {
      prevT = null;
      continue;
    }
    cur.push(`${cur.length === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`);
    prevT = p.t;
  }
  if (cur.length > 1) paths.push(cur.join(" "));
  const last = [...series].reverse().find((p): p is ValuePoint => p !== null);
  return { key, label, paths, color, faint, value: last ? fmt(last.raw) : "—" };
}

/**
 * Метка достройки для легенды: самая частая по ряду.
 *
 * Число дописанных секунд меняется от записи к записи, и метка по последней
 * точке прыгала бы между «t−4» и «t−5», двигая легенду. Самая частая описывает
 * линию честно; точную метку секунды даёт курсор.
 */
function fwdLagLabel(pts: TimelineIndexPoint[]): string {
  const counts = new Map<number, number>();
  for (const p of pts) {
    if (!p.fwd) continue;
    const est = p.fwd_estimated ?? 0;
    counts.set(est, (counts.get(est) ?? 0) + 1);
  }
  let best = 0;
  let bestN = 0;
  for (const [est, n] of counts) {
    if (n > bestN) {
      best = est;
      bestN = n;
    }
  }
  return `t−${REAL_DATA_LAG_S + best}`;
}

/**
 * Доллары со знаком: «+4.31», «−0.57», «0.00».
 *
 * Типографский минус, как у отсчёта оси: дефис рядом с числом читался бы как
 * диапазон.
 */
export function signedUsd(raw: string | undefined): string {
  const m = parse(raw);
  if (m === null) return "—";
  const abs = groupThousands(roundToFixed(Math.abs(m), 2));
  if (abs === "0.00") return abs;
  return `${m > 0 ? "+" : "−"}${abs}`;
}

/**
 * Подпись шкалы в долларах: знаков столько, сколько различает размах.
 *
 * Жёлоб шкалы вмещает восемь знаков, а на BTC уровень пятизначный: с двумя
 * знаками после точки подпись «75 641.72» в жёлоб не входит, а с нулём — на
 * размахе в пять долларов все четыре деления читались бы одним числом.
 */
function dollarsTick(v: number, span: number, signed: boolean): string {
  const decimals = span >= 50 ? 0 : span >= 5 ? 1 : 2;
  const abs = groupThousands(Math.abs(v).toFixed(decimals));
  if (!signed || Number(Math.abs(v).toFixed(decimals)) === 0) return abs;
  return `${v > 0 ? "+" : "−"}${abs}`;
}

/**
 * Строка легенды столбикового полотна.
 *
 * Значения тут пусты намеренно: у столбика нет «последнего значения» — на тихой
 * секунде его нет вовсе, и «—» в легенде читалось бы как «ставок не было за всё
 * окно». Числа даёт курсор, у которого есть корзина.
 */
function barLegend(key: string, label: string, color: string): TimelineLine {
  return { key, label, paths: [], color, faint: false, value: "" };
}

/** Координата нуля на полотне накопления — по той же шкале, что и линия. */
function zeroAt(points: TimelinePoint[], picked: boolean): number {
  const { lo, hi } = netRange(points, picked);
  return EDGE_H - ((0 - lo) / (hi - lo)) * EDGE_H;
}

/**
 * Деления шкалы значений: четыре штуки поперёк полотна.
 *
 * Четыре, а не два по краям, как было: по двум крайним числам наклон линии не
 * прочитать — между ними нет опоры, и «выросло вдвое» неотличимо от «выросло на
 * четверть». Четыре дают середину и обе четверти, и подписи при этом не
 * слипаются на высоте в сто с небольшим пикселей.
 */
const VALUE_TICKS = 4;

function valueTicks(
  lo: number,
  hi: number,
  y: (v: number) => number,
  fmt: (v: number) => string,
): TimelineTick[] {
  const out: TimelineTick[] = [];
  for (let i = 0; i < VALUE_TICKS; i++) {
    const v = hi - ((hi - lo) * i) / (VALUE_TICKS - 1);
    out.push({ at: y(v), label: fmt(v), anchor: "end" });
  }
  return out;
}

/**
 * Имена исходов из справочника рынка.
 *
 * Прочерка тут нет: подпись линии обязана называть сторону, а «—» на её месте
 * оставило бы читателя гадать, какая из двух перед ним.
 */
function firstName(t: Timeline, outcomes: OutcomeNames): string {
  return (t.first_asset_id ? outcomes[t.first_asset_id] : undefined) ?? "первый исход";
}

function secondName(t: Timeline, outcomes: OutcomeNames): string {
  return (t.second_asset_id ? outcomes[t.second_asset_id] : undefined) ?? "второй исход";
}

/** Цена на шкале: три знака — столько же, сколько у шага цены рынка. */
function priceLabel(v: number): string {
  return roundToFixed(Math.round(v * SCALE), 3);
}

/**
 * Шеры на шкале: тем же форматом, что в таблице накопления.
 *
 * Округление до шести знаков обязательно: деления шкалы считаются делением
 * отрезка на равные части, и «666.6666666666666» разбору денег не поддаётся —
 * подпись выходила прочерком, то есть шкала теряла середину.
 */
function sharesLabel(v: number): string {
  return formatShares(v.toFixed(6));
}

interface QuotePoint {
  t: number;
  v: number;
}

/**
 * Середина по двум сторонам образца.
 *
 * null там, где стороны нет: одностороннюю котировку нельзя свести к середине,
 * а подставить вместо неё цену единственной стороны значило бы нарисовать
 * скачок там, где стакан просто опустел.
 */
function midSeries(
  quotes: TimelineQuote[],
  sides: (q: TimelineQuote) => [string | undefined, string | undefined],
): (QuotePoint | null)[] {
  return quotes.map((q) => {
    const [bidRaw, askRaw] = sides(q);
    const bid = parse(bidRaw);
    const ask = parse(askRaw);
    if (bid === null || ask === null) return null;
    return { t: q.t, v: (bid + ask) / 2 / SCALE };
  });
}

function quoteLine(
  key: string,
  label: string,
  series: (QuotePoint | null)[],
  color: string,
  x: (s: number) => number,
  y: (v: number) => number,
): TimelineLine {
  const paths: string[] = [];
  let cur: string[] = [];
  for (const p of series) {
    if (p === null) {
      // Пробел РВЁТ линию: между образцами цена неизвестна, и соединённый
      // пробел был бы нарисованным движением, которого не наблюдали.
      if (cur.length > 1) paths.push(cur.join(" "));
      cur = [];
      continue;
    }
    cur.push(`${cur.length === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`);
  }
  if (cur.length > 1) paths.push(cur.join(" "));
  const last = [...series].reverse().find((p): p is QuotePoint => p !== null);
  return {
    key,
    label,
    paths,
    color,
    faint: false,
    value: last ? roundToFixed(Math.round(last.v * SCALE), 3) : "—",
  };
}

function line(
  key: string,
  label: string,
  series: (number | null)[],
  points: TimelinePoint[],
  color: string,
  faint: boolean,
  x: (s: number) => number,
  y: (v: number) => number,
  fmt: (v: number) => string,
): TimelineLine {
  const paths: string[] = [];
  let cur: string[] = [];
  series.forEach((v, i) => {
    if (v === null) {
      if (cur.length > 1) paths.push(cur.join(" "));
      cur = [];
      return;
    }
    const at = points[i]?.t ?? i;
    cur.push(`${cur.length === 0 ? "M" : "L"}${x(at).toFixed(1)},${y(v).toFixed(1)}`);
  });
  if (cur.length > 1) paths.push(cur.join(" "));
  const last = [...series].reverse().find((v): v is number => v !== null);
  return {
    key,
    label,
    paths,
    color,
    faint,
    value: last === undefined ? "—" : fmt(last),
  };
}

/**
 * Длина оси времени в секундах — ВСЁ ОКНО, а не пройденная его часть.
 *
 * # Почему не «до последней точки»
 *
 * Потому что тогда масштаб оси меняется на каждом опросе: на десятой секунде
 * окна десять секунд занимают всю ширину, на сотой — те же десять секунд
 * занимают десятую её часть. Линия при этом не растёт слева направо, а
 * непрерывно сжимается, и сравнить наклон в начале окна с наклоном в конце
 * нельзя вовсе — они нарисованы в разных масштабах. Замер оператора: «сразу
 * зафиксируем 5м ось X, и линия будет на ней нарастать, но без изменений
 * масштаба».
 *
 * # Почему всё-таки не жёсткие 300 секунд
 *
 * Потому что окна бывают не только пятиминутные: та же панель открывается на
 * пятнадцатиминутных. Длина берётся из справочника рынка (`window_end −
 * window_start`), а константы здесь нет вовсе — иначе на 15m линия упиралась бы
 * в правый край на трети окна.
 *
 * Нижняя граница — последняя точка ряда: окно, доторговываемое после своего
 * конца, не должно обрезать линию.
 */
function axisSpan(t: Timeline): number {
  const from = axisFrom(t);
  const lastT = Math.max(
    t.points[t.points.length - 1]?.t ?? 0,
    t.index?.[t.index.length - 1]?.t ?? 0,
  );
  let windowS = 0;
  if (t.window_start && t.window_end) {
    const start = Date.parse(t.window_start);
    const end = Date.parse(t.window_end);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      windowS = Math.round((end - start) / 1000);
    }
  }
  return Math.max(windowS - from, lastT - from, 1);
}

/**
 * Левый край оси: сколько секунд предстарта на ней стоит.
 *
 * С сервера, а не константой здесь: точки он раскладывал по своей оси, и
 * разъедься края — первая точка легла бы не на своё деление. Ноль у рынка без
 * окна серии и у старого ответа без поля: тогда ось начинается в t0, как раньше.
 */
function axisFrom(t: Timeline): number {
  const from = t.axis_from_s ?? 0;
  return t.window_start && Number.isFinite(from) && from < 0 ? from : 0;
}

/**
 * Деления оси времени.
 *
 * # Почему их много
 *
 * Шести делений на пятиминутку мало: между «+2:00» и «+3:00» шестьдесят секунд,
 * и куда попала точка, по такой оси не сказать. Замечание оператора: «точность —
 * минута, можно до секунды». Секундной сетки не бывает — триста подписей не
 * влезут ни в какую ширину, — поэтому сетка сгущена до полуминуты, а СЕКУНДУ
 * называет подсказка при наведении: там она точная, а не считанная на глаз.
 *
 * Потолок — одиннадцать подписей: дальше «+2:30» начинают слипаться на обычной
 * ширине карточки.
 *
 * Сетка круглая — по минутам и их долям, а не «span / 10»: подпись «+1:23» не
 * помогает соотнести точку с отсчётом окна, который оператор читает в шапке.
 */
const TICK_STEPS_S = [5, 10, 15, 30, 60, 120, 300, 600];

/**
 * Сколько подписей терпит ось.
 *
 * Двадцать одна: на пятиминутке это шаг в пятнадцать секунд, и подпись «+2:15»
 * шириной около 35 пикселей стоит на своих семидесяти свободно. Одиннадцати
 * было мало — между «+2:00» и «+2:30» тридцать секунд, и куда попала точка, по
 * такой оси всё ещё не сказать.
 */
const MAX_X_TICKS = 21;

/**
 * Сколько штрихов СЕТКИ терпит ось — больше, чем подписей.
 *
 * Двадцать семь: ровно столько даёт пятиминутка с полутора минутами предстарта
 * при пятнадцатисекундном шаге. Штрих — волос шириной в пиксель, и он не
 * слипается там, где слиплись бы подписи; а шаг сетки, выбранный по потолку
 * подписей, огрубел бы на этой оси до полуминуты — ровно то, от чего правка
 * «можно до секунды» уводила.
 */
const MAX_X_GRID = 27;

function xTicks(from: number, span: number, x: (s: number) => number): TimelineTick[] {
  const stepS = TICK_STEPS_S.find((s) => span / s <= MAX_X_GRID - 1) ?? span;
  const end = from + span;
  // Секунды делений. Кратны шагу ОТ НУЛЯ, а не от левого края: открытие окна
  // обязано стоять на делении, иначе подписи предстарта и окна шли бы со сдвигом.
  const secs: number[] = [];
  for (let s = Math.ceil(from / stepS) * stepS; s <= end; s += stepS) secs.push(s);
  // Левый край подписывается, даже когда не попал на круглый шаг: иначе ось не
  // говорит, с какой секунды предстарта она начинается.
  if (secs[0] !== from) secs.unshift(from);
  // Правый край подписывается ВСЕГДА, даже когда не попал на круглый шаг:
  // это конец окна, и без него ось не сообщает, чему равна её длина.
  if (secs[secs.length - 1] !== end) secs.push(end);

  // Подписей больше потолка — прореживаются через одну по ЧЁТНОСТИ ШАГА от нуля,
  // а не по номеру деления: так открытие окна всегда подписано, а края
  // подписаны отдельно — это три секунды, по которым ось вообще читается.
  const thin = secs.length > MAX_X_TICKS;
  const out: TimelineTick[] = secs.map((s, i) => {
    const tick: TimelineTick = { at: x(s), label: clock(s), anchor: "middle" };
    const edge = i === 0 || i === secs.length - 1;
    if (thin && !edge && s % (2 * stepS) !== 0) tick.minor = true;
    return tick;
  });
  const first = out[0];
  const last = out[out.length - 1];
  if (first) first.anchor = "start";
  if (last) last.anchor = "end";
  return out;
}

/**
 * Значения всех рядов в ОДНОЙ секунде окна — то, что показывает курсор.
 *
 * # Зачем отдельная функция, а не вьюмодель с параметром
 *
 * Вьюмодель считает пути, шкалы и деления — сотни точек на каждый вызов, — а
 * курсор двигается десятки раз в секунду. Пересобирать её на каждое движение
 * значило бы перерисовывать весь холст ради двух чисел под ним.
 *
 * # Почему значения ложатся в ЛЕГЕНДУ, а не во всплывающее окно
 *
 * Потому что легенда уже есть и уже показывает последнее значение каждой линии.
 * Курсор подменяет в ней числа, оставляя подписи и цвета на месте: ничего не
 * появляется, ничего не исчезает, высота карточки не меняется. Всплывающее окно
 * пришлось бы позиционировать, а на краю холста — ещё и переворачивать.
 */
export interface TimelineHover {
  /** Секунда окна под курсором, подписью. */
  at: string;
  /** Значение по КЛЮЧУ линии: up, down, market-avg, group-avg, market, group. */
  values: Record<string, string>;
  /** Приписка: сделки этой секунды, деньги и возраст стакана. */
  note: string;
}

/**
 * Значения рядов в секунде `sec`. null — ряда нет либо секунда вне оси.
 *
 * Котировка берётся ТОЛЬКО из своей корзины и не подтягивается из соседней:
 * пропуск образца означает, что цены в эту секунду мы не наблюдали, и подставить
 * туда прошлую значило бы выдать за наблюдение то, чего не было. На линии этот
 * же пропуск — разрыв.
 */
export function timelineHover(t: Timeline | undefined, sec: number | null): TimelineHover | null {
  if (!t || sec === null) return null;
  const step = Math.max(t.step_s, 1);
  const bucket = Math.round(sec / step) * step;
  const point = t.points.find((p) => p.t === bucket) ?? t.points[t.points.length - 1];
  const idx = t.index?.find((p) => p.t === bucket);
  if (!point) {
    // Накопления нет, а ряд индекса есть — предстарт без сделок. Курсор
    // показывает индекс, а не молчит: ради этого отрезка полотна и заведены.
    if (!t.index?.length) return null;
    return {
      at: clock(bucket),
      values: indexHoverValues(idx),
      note: indexHoverNote(t, idx),
    };
  }
  const quote = t.quotes.find((q) => q.t === bucket);

  const values: Record<string, string> = {
    up: mid(quote?.up_bid, quote?.up_ask),
    down: mid(quote?.down_bid, quote?.down_ask),
    "market-avg": priceOr(point.all_avg),
    "group-avg": priceOr(point.avg),
    market: formatShares(point.all_net),
    group: point.net === undefined ? "—" : formatShares(point.net),
    "up-taker": sharesOr(point.up_taker),
    "up-maker": sharesOr(point.up_maker),
    "down-taker": sharesOr(point.down_taker),
    "down-maker": sharesOr(point.down_maker),
    "in-up": sharesOr(point.in_up),
    "in-down": sharesOr(point.in_down),
    top: sharesOr(point.top),
    "all-taker": sharesOr(point.all_taker),
    "all-maker": sharesOr(point.all_maker),
    "all-flow": sharesOr(point.all_flow),
    "all-top": sharesOr(point.all_top),
    ...indexHoverValues(idx),
  };

  const note: string[] = [
    `${point.trades} ${plural(point.trades, "сделка", "сделки", "сделок")} в эту секунду`,
    `рынок ${formatDollars(point.all_edge)}`,
  ];
  if (point.edge !== undefined) note.push(`отобранные ${formatDollars(point.edge)}`);
  // Крупнейшая одиночная ставка — рядом с объёмом корзины: при равном объёме
  // «одна крупная» и «много мелких» это разные события.
  const top = point.top ?? point.all_top;
  if (top !== undefined) note.push(`крупнейшая ставка ${formatShares(top)} share`);
  note.push(quote ? `стакан ${quote.age_ms} мс` : "образца котировки нет");
  const indexNote = indexHoverNote(t, idx);
  if (indexNote !== "") note.push(indexNote);

  return { at: clock(point.t), values, note: note.join(" · ") };
}

/**
 * Значения ряда индекса в корзине курсора.
 *
 * Только из своей корзины, как у котировки: пропуск записи — это «не знаем», и
 * подставленное соседнее значение выдавало бы за наблюдение то, чего не было.
 */
function indexHoverValues(p: TimelineIndexPoint | undefined): Record<string, string> {
  const price = (raw: string | undefined) => (raw ? formatIndex(raw) : "—");
  const sigma = parse(p?.strike_sigma);
  return {
    index: price(p?.index),
    ahead: price(p?.ahead),
    // Метка секунды — точная, а не самая частая, как в подписи линии.
    fwd: p?.fwd ? `${formatIndex(p.fwd)} t−${REAL_DATA_LAG_S + (p.fwd_estimated ?? 0)}` : "—",
    "strike-forecast":
      p?.strike && p.strike_forecast
        ? `${formatIndex(p.strike)}${sigma !== null && sigma > 0 ? ` ±${roundToFixed(sigma, 2)}` : ""}`
        : "—",
    strike: p?.strike && !p.strike_forecast ? formatIndex(p.strike) : "—",
    raw: price(p?.raw),
    // Биржа под курсором — в СВОЕЙ валюте, без сдвига: это число сверяют с
    // терминалом биржи, а сдвинутое там не найти.
    spot: p?.spot ? `${formatIndex(p.spot)} USDT` : "—",
    deviation: signedUsd(p?.deviation),
    cushion: signedUsd(p?.cushion),
    "cushion-ahead": signedUsd(p?.cushion_ahead),
  };
}

/** Приписка курсора про индекс. Пусто — ряда индекса у ответа нет вовсе. */
function indexHoverNote(t: Timeline, p: TimelineIndexPoint | undefined): string {
  if (!t.index?.length) return "";
  return p ? `индекс ${p.age_ms} мс` : "записи индекса нет";
}

/** Середина по двум сторонам либо прочерк: односторонней середины не бывает. */
function mid(bid: string | undefined, ask: string | undefined): string {
  const b = parse(bid);
  const a = parse(ask);
  if (b === null || a === null) return "—";
  return roundToFixed(Math.round((b + a) / 2), 3);
}

function priceOr(raw: string | undefined): string {
  const m = parse(raw);
  return m === null ? "—" : roundToFixed(m, 3);
}

function sharesOr(raw: string | undefined): string {
  return raw === undefined ? "—" : formatShares(raw);
}

/**
 * Чей это состав — строкой в шапку карточки.
 *
 * Состав задаёт фильтр таблицы, а таблица лежит НИЖЕ графика и на экран вместе
 * с ним попадает не всегда. Без этой строки оператор читал бы линию, не видя,
 * чья она, — и первое же переключение фильтра сделало бы прежний вывод неверным
 * молча.
 *
 * Число берётся из ответа, а не считается здесь: сервер отбирает по ВСЕМ
 * кошелькам снимка, а до этой стороны доезжает лишь сотня строк таблицы.
 */
function pickedNote(t: Timeline): string {
  if (t.pick_reason) return "состав не отобран";
  const asked = t.picked_asked ?? 0;
  const picked = t.picked ?? 0;
  if (asked > 0) {
    // Отметки живут дольше окна, и часть отмеченных в нём не торговала. Сказать
    // «по 6 отмеченным», посчитав четверых, значило бы соврать суммой — тот же
    // довод, что у строки «Итого» с её полем missing.
    return picked === asked
      ? `по ${asked} отмеченным`
      : `по ${picked} отмеченным из ${asked}: остальные в этом окне не торговали`;
  }
  return `по ${picked} ${plural(picked, "кошельку", "кошелькам", "кошелькам")} фильтра`;
}

/**
 * «+2:19» от начала окна, «−1:30» до него. Та же форма, что у tauLabel в
 * раскрытых сделках.
 *
 * Знак у предстарта — типографский минус, как в «t−3» по всей карточке: дефис
 * «-1:30» на оси читался бы как диапазон.
 */
export function clock(sec: number): string {
  const sign = sec < 0 ? "−" : "+";
  const abs = Math.abs(sec);
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return m === 0 ? `${sign}${s} с` : `${sign}${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Размах с полями и полом.
 *
 * Пол не косметика: без него шкала подстраивалась бы под дрожание последнего
 * знака, и ровная линия выглядела бы горным хребтом.
 */
function paddedRange(values: number[], minSpan: number): { lo: number; hi: number } {
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { lo: 0, hi: minSpan };
  const span = hi - lo;
  if (span < minSpan) {
    const mid = (lo + hi) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  } else {
    lo -= span * 0.08;
    hi += span * 0.08;
  }
  return { lo, hi };
}

/** Число из строки ответа. null — поля не было либо оно пустое. */
function num(raw: string | undefined): number | null {
  const m: Micro | null = parse(raw);
  return m === null ? null : m / SCALE;
}

/** Шеры и цена читаются одинаково — имена стоят ради читаемости вызовов. */
const shares = num;
const price = num;

function beforeOpenNote(t: Timeline): string {
  // Пусто судится по ДЕНЬГАМ рынка и шерам группы — ровно по тем величинам,
  // которые ниже и печатаются. Нетто рынка сюда не входит: оно ноль всегда.
  const usdAll = parse(t.before_open_all_usd);
  const group = parse(t.before_open);
  if ((usdAll === null || usdAll === 0) && (group === null || group === 0)) return "";
  // Про рынок печатаются ДЕНЬГИ, а не шеры: нетто рынка тождественно ноль
  // (см. netChart), и «рынок 0 share» читалось бы как «до открытия не
  // торговали».
  const parts: string[] = [];
  if (t.before_open_all_usd) parts.push(`рынок ${formatDollars(t.before_open_all_usd)}`);
  if (!t.pick_reason) {
    parts.push(`отобранные ${formatShares(t.before_open)} share`);
  }
  if (parts.length === 0) return "";
  return `до открытия окна: ${parts.join(", ")}`;
}

/**
 * Строка «что первично».
 *
 * # Что считается
 *
 * Взаимная корреляция ПРИРАЩЕНИЙ — накопления и середины первого исхода, — на
 * сетке сдвигов до LEAD_MAX_S. Приращений, а не самих рядов: два накопительных
 * ряда коррелируют почти всегда просто потому, что оба растут, и коэффициент по
 * ним отвечал бы на вопрос «шло ли время».
 *
 * # Почему у неё есть пол разрешения
 *
 * Потому что без него она врёт на главном вопросе панели. Сделка приезжает
 * лентой с задержкой около секунды, котировка снимается тиком со своим
 * возрастом; сдвиг меньше LEAD_FLOOR_S — это разница двух задержек, а не
 * свойство рынка. Печатать его числом значило бы выдать нашу проводку за
 * поведение участников.
 */
function leadOf(t: Timeline): { lead: string; leadHint: string } {
  // Считается по НЕТТО ГРУППЫ, а без группы — по средней цене входа рынка.
  // Не по нетто рынка: оно тождественно ноль (см. netChart), и корреляция с ним
  // мерила бы шум округления пар.
  const useGroup = !t.pick_reason;
  const flow = deltas(
    t.points.map((p) => (useGroup ? shares(p.net) : price(p.all_avg))),
  );
  const mid = alignQuotes(t);
  const best = bestLag(flow, mid, Math.max(1, Math.round(LEAD_MAX_S / t.step_s)));

  const hint =
    "Сдвиг взаимной корреляции приращений: накопление против середины первого исхода. " +
    `Меньше ${LEAD_FLOOR_S} с не различимо ничем — столько стоит путь сделки до нас ` +
    "плюс возраст стакана, и такой сдвиг описывал бы нашу проводку, а не рынок.";

  if (best === null) {
    return {
      lead: `первичность не считается: нужно ${LEAD_MIN_POINTS} общих точек обоих рядов`,
      leadHint: hint,
    };
  }
  const shiftS = best.lag * t.step_s;
  const r = (best.r >= 0 ? "+" : "−") + Math.abs(best.r).toFixed(2);
  if (Math.abs(shiftS) < LEAD_FLOOR_S) {
    return { lead: `кто первичен — не различимо (сдвига нет, r = ${r})`, leadHint: hint };
  }
  const who =
    shiftS > 0
      ? `поток опережает котировку на ${Math.abs(shiftS)} с`
      : `котировка опережает поток на ${Math.abs(shiftS)} с`;
  return { lead: `${who}, r = ${r}`, leadHint: hint };
}

/**
 * Середина первого исхода, разложенная по СЕТКЕ НАКОПЛЕНИЯ.
 *
 * Разложенная, а не взятая как есть: у котировки свои корзины, и корреляция по
 * двум рядам разной длины сравнивала бы точки разных секунд. Корзина без
 * образца остаётся null и в пару не попадает.
 */
function alignQuotes(t: Timeline): (number | null)[] {
  const byBucket = new Map<number, number>();
  for (const q of t.quotes) {
    const bid = parse(q.up_bid);
    const ask = parse(q.up_ask);
    if (bid === null || ask === null) continue;
    byBucket.set(q.t, (bid + ask) / 2 / SCALE);
  }
  return deltas(t.points.map((p) => byBucket.get(p.t) ?? null));
}

/** Приращения ряда. Пара с пропуском даёт null: разность неизвестна. */
function deltas(series: (number | null)[]): (number | null)[] {
  return series.map((v, i) => {
    if (i === 0 || v === null) return null;
    const prev = series[i - 1];
    return prev === null || prev === undefined ? null : v - prev;
  });
}

/**
 * Насколько коэффициент обязан быть больше, чтобы считаться БОЛЬШИМ.
 *
 * Сотая доля: ниже неё разница между двумя сдвигами — это разница в шуме, а не
 * в силе связи.
 */
const LEAD_TIE = 0.01;

/**
 * Лучший сдвиг и его коэффициент. null — общих точек меньше порога.
 *
 * # Почему ничья разрешается в пользу МЕНЬШЕГО сдвига
 *
 * Потому что равный коэффициент означает, что данные этих сдвигов НЕ РАЗЛИЧАЮТ:
 * так бывает на периодичном потоке, где сдвиг на период неотличим от нулевого.
 * Выбери из них больший — и панель объявила бы фору, которой в данных нет,
 * причём с уверенным числом рядом. Меньший сдвиг — это ответ «связь есть, форы
 * не видно», и он единственный честный.
 */
export function bestLag(
  a: (number | null)[],
  b: (number | null)[],
  maxLag: number,
): { lag: number; r: number } | null {
  let best: { lag: number; r: number } | null = null;
  for (let lag = -maxLag; lag <= maxLag; lag++) {
    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = 0; i < a.length; i++) {
      const av = a[i];
      const bv = b[i + lag];
      if (av === null || av === undefined || bv === null || bv === undefined) continue;
      xs.push(av);
      ys.push(bv);
    }
    if (xs.length < LEAD_MIN_POINTS) continue;
    const r = pearson(xs, ys);
    if (r === null) continue;
    if (best === null) {
      best = { lag, r };
      continue;
    }
    const stronger = Math.abs(r) > Math.abs(best.r) + LEAD_TIE;
    const tied = Math.abs(r) >= Math.abs(best.r) - LEAD_TIE;
    if (stronger || (tied && Math.abs(lag) < Math.abs(best.lag))) best = { lag, r };
  }
  return best;
}

/** Коэффициент Пирсона. null на вырожденном ряде: константа ни с чем не связана. */
export function pearson(xs: number[], ys: number[]): number | null {
  const n = xs.length;
  if (n < 2) return null;
  const mx = xs.reduce((s, v) => s + v, 0) / n;
  const my = ys.reduce((s, v) => s + v, 0) / n;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < n; i++) {
    const a = (xs[i] as number) - mx;
    const b = (ys[i] as number) - my;
    num += a * b;
    dx += a * a;
    dy += b * b;
  }
  if (dx === 0 || dy === 0) return null;
  return num / Math.sqrt(dx * dy);
}
